import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useEnhancedBrandStore } from '../../../stores/enhancedBrandStore'
import { PriceTier } from '../../../types/brand'
import { XMarkIcon } from '@heroicons/react/24/outline'
import echarts from '../../../lib/echarts'

const PRICE_COLORS = {
  budget: '#10b981',
  'mid-range': '#3b82f6',
  premium: '#f59e0b',
  luxury: '#dc2626',
  unknown: '#71717a'
}

const PRICE_LABELS = {
  budget: 'Budget',
  'mid-range': 'Mid-Range',
  premium: 'Premium',
  luxury: 'Luxury',
  unknown: 'Unknown'
}

export function InteractivePriceChart() {
  const { filteredBrands, allBrands, filters, setPriceTierFilter } = useEnhancedBrandStore()

  const chartData = useMemo(() => {
    const counts = filteredBrands.reduce((acc, brand) => {
      if (brand.price_tier in PRICE_LABELS) {
        acc[brand.price_tier] = (acc[brand.price_tier] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    // Order by price tier hierarchy
    const orderedTiers: PriceTier[] = ['budget', 'mid-range', 'premium', 'luxury', 'unknown']
    
    return orderedTiers
      .filter(tier => (counts[tier] || 0) > 0)
      .map(tier => ({
        name: PRICE_LABELS[tier],
        value: counts[tier] || 0,
        key: tier,
        itemStyle: {
          color: PRICE_COLORS[tier],
          shadowBlur: filters.priceTier.length > 0 && filters.priceTier.includes(tier) ? 6 : 0,
          shadowColor: filters.priceTier.length > 0 && filters.priceTier.includes(tier) ? 'rgba(0, 0, 0, 0.4)' : 'transparent'
        }
      }))
  }, [filteredBrands, filters.priceTier])

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const data = params[0]
        const percentage = ((data.value / filteredBrands.length) * 100).toFixed(1)
        return `<strong>${data.name}</strong><br/>${data.value} brands (${percentage}%)`
      }
    },
    xAxis: {
      type: 'category',
      data: chartData.map(item => item.name),
      axisLabel: {
        interval: 0,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Brands',
      nameTextStyle: {
        fontSize: 12
      }
    },
    series: [
      {
        type: 'bar',
        data: chartData,
        barWidth: '50%',
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ],
    grid: {
      top: '10%',
      left: '10%',
      right: '5%',
      bottom: '15%'
    }
  }

  const handleChartClick = (params: any) => {
    const clickedKey = chartData.find(item => item.name === params.name)?.key
    if (!clickedKey) return

    const currentFilters = filters.priceTier
    const isSelected = currentFilters.includes(clickedKey)

    if (isSelected) {
      setPriceTierFilter(currentFilters.filter(f => f !== clickedKey))
    } else {
      setPriceTierFilter([...currentFilters, clickedKey])
    }
  }

  const clearFilter = (filterValue: PriceTier) => {
    setPriceTierFilter(filters.priceTier.filter(f => f !== filterValue))
  }

  const onEvents = {
    click: handleChartClick
  }

  const hasActiveFilters = filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.productCategories.length > 0 || 
                          filters.states.length > 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Chart */}
      <div className="space-y-3">
        {hasActiveFilters && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded text-xs font-medium">
              FILTERED VIEW
            </div>
            <span className="text-gray-600 dark:text-gray-400">
              Showing {filteredBrands.length} of {allBrands.length} brands
            </span>
          </div>
        )}
        <div className="h-64">
          <ReactECharts
            option={option}
            style={{ height: '100%', width: '100%' }}
            onEvents={onEvents}
            echarts={echarts}
          />
        </div>
      </div>

      {/* Active Filters */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Active Price Filters
          </h4>
          {filters.priceTier.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No filters applied. Click chart bars to filter.
            </p>
          ) : (
            <div className="space-y-2">
              {filters.priceTier.map((filter) => (
                <div
                  key={filter}
                  className="flex items-center justify-between bg-yellow-50 dark:bg-yellow-900/30 px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: PRICE_COLORS[filter] }}
                    />
                    <span className="text-sm text-yellow-700 dark:text-yellow-300">
                      {PRICE_LABELS[filter]}
                    </span>
                  </div>
                  <button
                    onClick={() => clearFilter(filter)}
                    className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setPriceTierFilter([])}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Clear all price filters
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            💡 <strong>Tip:</strong> Click any bar to filter brands by that price tier.
          </p>
        </div>
      </div>
    </div>
  )
}